import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import MailOutlineOutlinedIcon from "@mui/icons-material/MailOutlineOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";

import axios from "../../helpers/axios";
import { generatePublicUrl } from "../../urlConfig";
import CallButtonBar from "../../components/call/CallButtonBar";

/*
 * Профиль участника чата для АДМИНКИ: все данные пользователя
 * (как в «Пользователях»), без ухода из чата. Редактор, безопасность,
 * роль и баланс — кнопкой «Управление» (раздел «Пользователи», новая вкладка).
 */

const getId = (value) => String(value?._id || value?.id || value || "").trim();

const ROLE_LABELS = {
    user: "Пользователь",
    admin: "Администратор",
    partner: "Партнёр",
    superAdmin: "Супер-администратор",
    storeOwner: "Владелец офиса",
    officeWorker: "Сотрудник офиса",
    cityAdmin: "Городской администратор",
};

const TABS = [
    { key: "profile", label: "Профиль" },
    { key: "subscriptions", label: "Подписки" },
    { key: "cart", label: "Корзина" },
    { key: "desires", label: "Избранное" },
    { key: "views", label: "Просмотры" },
];

// Откуда брать список в ответе сервера (берём первый найденный массив).
const LIST_SOURCES = {
    cart: {
        url: (id) => `/admin/getUserCartProduct/${id}`,
        keys: ["cart.cartItems", "cartItems", "cart", "items", "products"],
        empty: "Корзина пуста.",
    },
    desires: {
        url: (id) => `/admin/getUserDesiresProduct/${id}`,
        keys: ["desires.desiresItems", "desiresItems", "desires", "items", "products"],
        empty: "Понравившихся товаров нет.",
    },
    views: {
        url: (id) => `/admin/getUserProductViews/${id}`,
        keys: ["productsHistory", "productViews", "history", "views", "items", "products"],
        empty: "История просмотров пуста.",
    },
};

const pickList = (data, keys) => {
    if (Array.isArray(data)) return data;

    for (const key of keys) {
        const value = key.split(".").reduce((item, part) => item?.[part], data);
        if (Array.isArray(value)) return value;
    }

    return [];
};

const imageSrc = (value) => {
    const image = String(value || "");
    if (!image) return "";
    if (/^(https?:)?\/\//i.test(image) || /^(blob:|data:image\/)/i.test(image) || image.startsWith("/media/")) return image;
    return generatePublicUrl(image);
};

const getUserName = (user = {}) => {
    const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
    return name || user.username || user.email || "Пользователь";
};

const getInitials = (user = {}) => {
    const first = String(user.firstName || "").trim()[0] || "";
    const last = String(user.lastName || "").trim()[0] || "";
    return (first || last ? `${first}${last}` : String(user.username || user.email || "U")[0] || "U").toUpperCase();
};

const formatDate = (value, withTime = true) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    return date.toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
    });
};

const formatClock = (date) => date.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });

const formatPresence = (online, lastSeenAt) => {
    if (online) return "в сети";
    if (!lastSeenAt) return "не в сети";

    const date = new Date(lastSeenAt);
    const time = date.getTime();
    if (!Number.isFinite(time)) return "не в сети";

    const minutes = Math.floor(Math.max(0, Date.now() - time) / 60000);
    if (minutes < 1) return "был(а) в сети только что";
    if (minutes < 60) return `был(а) в сети ${minutes} мин назад`;

    const now = new Date();
    if (now.toDateString() === date.toDateString()) return `был(а) в сети сегодня в ${formatClock(date)}`;

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (yesterday.toDateString() === date.toDateString()) return `был(а) в сети вчера в ${formatClock(date)}`;

    return `был(а) в сети ${formatDate(date)}`;
};

const getGender = (value) => (
    value === "male" ? "Мужской" : value === "female" ? "Женский" : value
);

const getRegion = (user = {}) => user.regionName || user.region?.name || user.region?.label || "";

const getErrorText = (error, fallback) => (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
);

export default function AdminUserProfileModal({
    open = true,
    userId,
    user = null,
    presence = null,
    onClose,
}) {
    const targetId = getId(userId || user);
    const currentUserId = useSelector((state) => getId(state.auth?.user));

    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [reload, setReload] = useState(0);
    const [tab, setTab] = useState("profile");
    const [lists, setLists] = useState({});
    const [copiedKey, setCopiedKey] = useState("");
    const copyTimerRef = useRef(null);
    // Какие вкладки уже запрошены (чтобы не грузить повторно).
    const requestedRef = useRef(new Set());

    // Полные данные пользователя — как в «Пользователях».
    useEffect(() => {
        if (!open || !targetId) return undefined;

        let cancelled = false;
        setLoading(true);
        setError("");

        axios.get(`/admin/getUserById/${encodeURIComponent(targetId)}`)
            .then(({ data }) => {
                if (cancelled) return;
                setDetails(data?.user || null);
                if (!data?.user) setError("Пользователь не найден");
            })
            .catch((requestError) => {
                if (!cancelled) setError(getErrorText(requestError, "Не удалось загрузить профиль"));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, targetId, reload]);

    // Сменили человека — начинаем с первой вкладки.
    useEffect(() => {
        setTab("profile");
        setLists({});
        setDetails(null);
        requestedRef.current = new Set();
    }, [targetId]);

    // Корзина, избранное, просмотры — грузим, когда открыли вкладку.
    useEffect(() => {
        const source = LIST_SOURCES[tab];
        const requestKey = `${targetId}:${tab}`;
        if (!open || !source || !targetId || requestedRef.current.has(requestKey)) return undefined;

        requestedRef.current.add(requestKey);
        let cancelled = false;
        let done = false;
        setLists((prev) => ({ ...prev, [tab]: { loading: true, items: [], error: "" } }));

        axios.get(source.url(encodeURIComponent(targetId)))
            .then(({ data }) => {
                if (cancelled) return;
                done = true;
                setLists((prev) => ({ ...prev, [tab]: { loading: false, items: pickList(data, source.keys), error: "" } }));
            })
            .catch((requestError) => {
                if (cancelled) return;
                done = true;
                const status = requestError?.response?.status;
                // 404 у этих ручек значит «пусто».
                setLists((prev) => ({
                    ...prev,
                    [tab]: {
                        loading: false,
                        items: [],
                        error: status === 404 ? "" : getErrorText(requestError, "Не удалось загрузить список"),
                    },
                }));
            });

        return () => {
            // Ушли раньше ответа — при возврате на вкладку запросим заново.
            cancelled = true;
            if (!done) requestedRef.current.delete(requestKey);
        };
    }, [open, tab, targetId]);

    useEffect(() => {
        if (!open) return undefined;

        const onKeyDown = (event) => {
            if (event.key === "Escape") onClose?.();
        };

        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [open, onClose]);

    useEffect(() => () => {
        if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current);
    }, []);

    const copyValue = useCallback(async (key, value) => {
        const text = String(value || "").trim();
        if (!text) return;

        let copied = false;

        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
                copied = true;
            } else {
                const textarea = document.createElement("textarea");
                textarea.value = text;
                textarea.setAttribute("readonly", "");
                textarea.style.position = "fixed";
                textarea.style.opacity = "0";
                document.body.appendChild(textarea);
                textarea.select();
                copied = document.execCommand("copy");
                document.body.removeChild(textarea);
            }
        } catch (_error) {
            copied = false;
        }

        if (!copied) return;

        setCopiedKey(key);
        if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current);
        copyTimerRef.current = window.setTimeout(() => setCopiedKey(""), 1400);
    }, []);

    // Пока полные данные грузятся — показываем то, что пришло из чата.
    const person = useMemo(() => ({
        ...(user && typeof user === "object" ? user : {}),
        ...(details || {}),
    }), [details, user]);

    if (!open || !targetId || typeof document === "undefined") return null;

    const name = getUserName(person);
    const online = !!presence?.online;
    const lastSeenAt = presence?.lastSeenAt || person.lastSeenAt || person.lastActiveAt || null;
    const presenceLabel = formatPresence(online, lastSeenAt);
    const active = person.isActive !== false;
    const emailVerified = !!(person.isActivated || person.emailVerifiedAt);
    const region = getRegion(person);
    const avatar = imageSrc(person.profilePicture);
    const subscriptions = Array.isArray(person.subscriptions) ? person.subscriptions : [];
    const isSelf = targetId === currentUserId;

    const copyButton = (key, value, label) => (
        <button
            type="button"
            className={`chat-user-profile-AO__copy ${copiedKey === key ? "is-copied" : ""}`}
            onClick={() => copyValue(key, value)}
            title={copiedKey === key ? "Скопировано" : `Скопировать ${label}`}
            aria-label={copiedKey === key ? "Скопировано" : `Скопировать ${label}`}
        >
            {copiedKey === key ? <CheckOutlinedIcon /> : <ContentCopyOutlinedIcon />}
        </button>
    );

    const field = (label, value, options = {}) => {
        const empty = value === null || value === undefined || value === "";

        return (
            <div className={`chat-user-profile-AO__field ${options.full ? "is-full" : ""}`} key={label}>
                <span>{label}</span>
                <div>
                    {empty ? (
                        <b className="is-empty">—</b>
                    ) : options.href ? (
                        <a href={options.href}>{value}</a>
                    ) : (
                        <b>{value}</b>
                    )}
                    {!empty && options.copy ? copyButton(options.copy, value, label.toLowerCase()) : null}
                </div>
            </div>
        );
    };

    const openManagement = () => {
        window.open(`/users?notificationUserId=${encodeURIComponent(targetId)}`, "_blank", "noopener");
    };

    const renderProfile = () => (
        <>
            {details && !region ? (
                <div className="chat-user-profile-AO__warning">Регион в профиле не указан.</div>
            ) : null}

            <section className="chat-user-profile-AO__section">
                <h4>Контакты</h4>
                <div className="chat-user-profile-AO__grid">
                    {field("Email", person.email, { href: person.email ? `mailto:${person.email}` : "", copy: "email", full: true })}
                    {field("Телефон", person.contactNumber, { href: person.contactNumber ? `tel:${person.contactNumber}` : "", copy: "phone" })}
                    {field("Резервный телефон", person.backupNumber, { href: person.backupNumber ? `tel:${person.backupNumber}` : "", copy: "backup" })}
                </div>
            </section>

            <section className="chat-user-profile-AO__section">
                <h4>Основные данные</h4>
                <div className="chat-user-profile-AO__grid">
                    {field("Имя", person.firstName)}
                    {field("Фамилия", person.lastName)}
                    {field("Дата рождения", person.birthData)}
                    {field("Пол", getGender(person.gender))}
                    {field("Регион", region)}
                    {field("Организация", person.nameOrganization, { copy: "organization" })}
                </div>
            </section>

            <section className="chat-user-profile-AO__section">
                <h4>Финансы</h4>
                <div className="chat-user-profile-AO__grid">
                    {field("Личный баланс", details ? `${Number(person.balance || 0).toLocaleString("ru-RU")} ₽` : "")}
                    {field("Подписок", details ? `${subscriptions.filter((item) => item?.isCompleted).length} из ${subscriptions.length}` : "")}
                </div>
            </section>

            <section className="chat-user-profile-AO__section">
                <h4>Система</h4>
                <div className="chat-user-profile-AO__grid">
                    {field("Роль", ROLE_LABELS[person.role] || person.role)}
                    {field("Статус", details ? (active ? "Активен" : "Отключён") : "")}
                    {field("Email", details ? (emailVerified ? "Подтверждён" : "Не подтверждён") : "")}
                    {field("Активность", presenceLabel)}
                    {field("Регистрация", formatDate(person.createdAt))}
                    {field("Обновлён", formatDate(person.updatedAt))}
                    {field("ID", targetId, { copy: "id", full: true })}
                </div>
            </section>
        </>
    );

    const renderSubscriptions = () => {
        if (!details) return <div className="chat-user-profile-AO__state">Загружаем…</div>;
        if (!subscriptions.length) return <div className="chat-user-profile-AO__state">Подписок нет.</div>;

        return (
            <div className="chat-user-profile-AO__list">
                {subscriptions.map((item) => {
                    const title = item?.mainPage?.title || item?.mainPage?.name || "Без названия";
                    const picture = imageSrc(item?.mainPage?.itemPictures?.[0]?.img);

                    return (
                        <div className="chat-user-profile-AO__item" key={item._id || title}>
                            <div className="chat-user-profile-AO__thumb">{picture ? <img src={picture} alt="" /> : null}</div>
                            <div className="chat-user-profile-AO__item-main">
                                <b>{title}</b>
                                <span>{item?.date ? formatDate(item.date) : "—"}</span>
                            </div>
                            <em className={item?.isCompleted ? "is-success" : "is-muted"}>
                                {item?.isCompleted ? "Подписан" : "Не подписан"}
                            </em>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderProducts = (key) => {
        const state = lists[key];
        if (!state || state.loading) return <div className="chat-user-profile-AO__state">Загружаем…</div>;
        if (state.error) return <div className="chat-user-profile-AO__state is-error">{state.error}</div>;
        if (!state.items.length) return <div className="chat-user-profile-AO__state">{LIST_SOURCES[key].empty}</div>;

        return (
            <div className="chat-user-profile-AO__list">
                {state.items.map((item, index) => {
                    const product = item?.product && typeof item.product === "object" ? item.product : item;
                    const title = product?.fullName || product?.name || item?.fullName || "Товар";
                    const picture = imageSrc(product?.productPictures?.[0]?.img || item?.img);

                    return (
                        <div className="chat-user-profile-AO__item" key={`${item?._id || index}-${item?.createdAt || ""}`}>
                            <div className="chat-user-profile-AO__thumb">{picture ? <img src={picture} alt="" /> : null}</div>
                            <div className="chat-user-profile-AO__item-main">
                                <b>{title}</b>
                                <span>{item?.createdAt ? formatDate(item.createdAt) : "—"}</span>
                            </div>
                            {key === "cart" ? <em>× {item?.quantity || 1}</em> : null}
                        </div>
                    );
                })}
            </div>
        );
    };

    const modal = (
        <div className="chat-user-profile-AO" onMouseDown={onClose} role="presentation">
            <section
                className="chat-user-profile-AO__panel"
                role="dialog"
                aria-modal="true"
                aria-label={`Профиль: ${name}`}
                onMouseDown={(event) => event.stopPropagation()}
            >
                <div className="chat-user-profile-AO__head">
                    <span>Профиль пользователя</span>
                    <div>
                        <button type="button" onClick={() => setReload((value) => value + 1)} title="Обновить" aria-label="Обновить">
                            <RefreshRoundedIcon />
                        </button>
                        <button type="button" onClick={onClose} title="Закрыть" aria-label="Закрыть">
                            <CloseOutlinedIcon />
                        </button>
                    </div>
                </div>

                <div className="chat-user-profile-AO__hero">
                    <div className="chat-user-profile-AO__avatar">
                        {avatar ? <img src={avatar} alt="" /> : <span>{getInitials(person)}</span>}
                        <i className={online ? "is-online" : ""} />
                    </div>

                    <div className="chat-user-profile-AO__identity">
                        <h3>{name}</h3>
                        {person.username ? (
                            <div className="chat-user-profile-AO__username">
                                <span>@{person.username}</span>
                                {copyButton("username", `@${person.username}`, "ник")}
                            </div>
                        ) : null}
                        <p className={online ? "is-online" : ""}>{presenceLabel}</p>
                        <div className="chat-user-profile-AO__chips">
                            {person.role ? <span>{ROLE_LABELS[person.role] || person.role}</span> : null}
                            {details ? <span className={active ? "is-success" : "is-danger"}>{active ? "Активен" : "Отключён"}</span> : null}
                            {details ? (
                                <span className={emailVerified ? "is-success" : "is-warning"}>
                                    {emailVerified ? "Email подтверждён" : "Email не подтверждён"}
                                </span>
                            ) : null}
                        </div>
                    </div>
                </div>

                <div className="chat-user-profile-AO__actions">
                    {person.email ? (
                        <a href={`mailto:${person.email}`}><MailOutlineOutlinedIcon /> Написать на email</a>
                    ) : null}
                    {person.contactNumber ? (
                        <a href={`tel:${person.contactNumber}`}><PhoneOutlinedIcon /> Позвонить на телефон</a>
                    ) : null}
                    {!isSelf ? (
                        // Звонок в приложении: карточку закрываем, чтобы не закрывала окно звонка.
                        <div className="chat-user-profile-AO__call" onClickCapture={() => window.setTimeout(() => onClose?.(), 0)}>
                            <CallButtonBar peerId={targetId} peerName={name} chatId={null} isGroup={false} />
                        </div>
                    ) : null}
                    <button type="button" className="is-primary" onClick={openManagement}>
                        <OpenInNewOutlinedIcon /> Управление
                    </button>
                </div>

                <div className="chat-user-profile-AO__tabs" role="tablist">
                    {TABS.map((item) => (
                        <button
                            type="button"
                            role="tab"
                            key={item.key}
                            aria-selected={tab === item.key}
                            className={tab === item.key ? "is-active" : ""}
                            onClick={() => setTab(item.key)}
                        >
                            {item.label}
                            {item.key === "subscriptions" && subscriptions.length ? <b>{subscriptions.length}</b> : null}
                        </button>
                    ))}
                </div>

                <div className="chat-user-profile-AO__body">
                    {loading && !details ? (
                        <div className="chat-user-profile-AO__loading"><i /> Загружаем все данные…</div>
                    ) : null}
                    {error ? (
                        <div className="chat-user-profile-AO__state is-error" role="alert">
                            {error}
                            <button type="button" onClick={() => setReload((value) => value + 1)}>Повторить</button>
                        </div>
                    ) : null}

                    {tab === "profile" ? renderProfile() : null}
                    {tab === "subscriptions" ? renderSubscriptions() : null}
                    {LIST_SOURCES[tab] ? renderProducts(tab) : null}
                </div>
            </section>
        </div>
    );

    return createPortal(modal, document.body);
}
